import api from './api';

// Server serializes the AssetVisibility enum as a string (JsonStringEnumConverter).
export type AssetVisibility = 'Private' | 'Shared';

// On the wire, ASP.NET still binds `[FromForm] int visibility` to the underlying enum value.
export const AssetVisibilityValue = { Private: 0, Shared: 1 } as const;

export interface AssetDto {
  id: string;
  name: string;
  description?: string | null;
  assetType: string;
  fileExtension: string;
  fileSizeBytes: number;
  userId: string;
  visibility: AssetVisibility;
  createdAt: string;
  updatedAt: string;
  tags: string[];
  typeInfo?: Record<string, unknown> | null;
  assetPackId?: string | null;
  assetPackName?: string | null;
}

export interface AssetPackDto {
  id: string;
  packName: string;
  packDescription?: string | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  assetCount: number;
}

export interface AssetSearchMatch {
  asset: AssetDto;
  score: number;
  downloadUrl?: string | null;
}

export interface AssetSearchResult {
  assets: AssetSearchMatch[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export type AssetSortBy = 'CreatedAt' | 'UpdatedAt' | 'Name' | 'Size';

export interface SearchAssetsParams {
  searchTerm?: string;
  assetType?: string;
  tags?: string[];
  assetPackId?: string;
  page?: number;
  pageSize?: number;
  sortBy?: AssetSortBy;
  sortDescending?: boolean;
  strict?: boolean;
  includePrivate?: boolean;
}

export async function searchAssets(params: SearchAssetsParams = {}): Promise<AssetSearchResult> {
  // ASP.NET model-binding for `string[]` expects repeated keys (?tags=a&tags=b),
  // not axios's default bracketed form (?tags[]=a).
  const { data } = await api.get<AssetSearchResult>('assets/search', {
    params,
    paramsSerializer: { indexes: null }
  });
  return data;
}

export async function getAvailableTags(includePrivate = false): Promise<string[]> {
  const { data } = await api.get<string[]>('assets/search/tags', { params: { includePrivate } });
  return data;
}

export async function getAvailableAssetTypes(includePrivate = false): Promise<string[]> {
  const { data } = await api.get<string[]>('assets/search/types', { params: { includePrivate } });
  return data;
}

export async function getAssetPacks(page = 1, pageSize = 100): Promise<AssetPackDto[]> {
  const { data } = await api.get<AssetPackDto[]>('assetpacks', { params: { page, pageSize } });
  return data;
}

export async function getAsset(assetId: string): Promise<AssetDto> {
  const { data } = await api.get<AssetDto>(`assets/${assetId}`);
  return data;
}

export async function getDownloadUrl(assetId: string, expirationHours = 24): Promise<string> {
  const { data } = await api.get<{ downloadUrl: string }>(
    `assets/${assetId}/download-url`,
    { params: { expirationHours } }
  );
  return data.downloadUrl;
}

export async function deleteAsset(assetId: string): Promise<void> {
  await api.delete(`assets/${assetId}`);
}

export interface BulkDeleteFailureItem {
  assetId: string;
  error: string;
}

export interface BulkDeleteResult {
  deletedIds: string[];
  notFoundIds: string[];
  failures: BulkDeleteFailureItem[];
}

export async function bulkDeleteAssets(assetIds: string[]): Promise<BulkDeleteResult> {
  const { data } = await api.post<BulkDeleteResult>('assets/bulk-delete', { assetIds });
  return data;
}

export interface AssetUploadResult {
  assetId: string;
  message: string;
  isExistingAsset: boolean;
}

export interface UploadAssetParams {
  file: File;
  name: string;
  description?: string;
  assetType: string;
  visibility: AssetVisibility;
  tags?: string[];
  assetPackId?: string;
  licenseId?: string;
  licenseText?: string;
  loopable?: boolean;
  author?: string;
  sourceName?: string;
  sourceUrl?: string;
}

export async function uploadAsset(params: UploadAssetParams): Promise<AssetUploadResult> {
  const form = new FormData();
  form.append('name', params.name);
  if (params.description) form.append('description', params.description);
  form.append('assetType', params.assetType);
  form.append('visibility', String(AssetVisibilityValue[params.visibility]));
  if (params.tags && params.tags.length > 0) form.append('tags', params.tags.join(','));
  if (params.assetPackId) form.append('assetPackId', params.assetPackId);
  if (params.licenseId) form.append('licenseId', params.licenseId);
  if (params.licenseText) form.append('licenseText', params.licenseText);
  if (params.loopable !== undefined) form.append('loopable', String(params.loopable));
  if (params.author) form.append('author', params.author);
  if (params.sourceName) form.append('sourceName', params.sourceName);
  if (params.sourceUrl) form.append('sourceUrl', params.sourceUrl);
  form.append('file', params.file);

  const { data } = await api.post<AssetUploadResult>('assets', form);
  return data;
}

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  estimatedUsd: number;
}

export interface BulkImportFileResult {
  fileName: string;
  assetId?: string | null;
  isExistingAsset: boolean;
  description?: string | null;
  tags?: string[] | null;
  llmGenerated: boolean;
  usage?: TokenUsage | null;
  error?: string | null;
}

export interface BulkImportResponse {
  results: BulkImportFileResult[];
  assetPackId?: string | null;
  totalUsage?: TokenUsage | null;
}

export interface BulkImportParams {
  files: File[];
  assetPackName?: string;
  assetPackDescription?: string;
  tags?: string[];
}

export const BULK_IMPORT_MAX_FILES = 50;

export const ASSET_PAGE_SIZE_OPTIONS = [12, 24, 48, 96] as const;
export const ASSET_PAGE_SIZE_DEFAULT = 24;
export const ASSET_PAGE_SIZE_STORAGE_KEY = 'adminAssets.pageSize';

export async function bulkImport(params: BulkImportParams): Promise<BulkImportResponse> {
  const form = new FormData();
  for (const file of params.files) {
    form.append('files', file);
  }
  if (params.assetPackName) form.append('assetPackName', params.assetPackName);
  if (params.assetPackDescription) form.append('assetPackDescription', params.assetPackDescription);
  if (params.tags && params.tags.length > 0) form.append('tags', params.tags.join(','));

  const { data } = await api.post<BulkImportResponse>('assets/bulk-import', form);
  return data;
}
